import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import CampaignIcon from '@mui/icons-material/Campaign';
import { fetchPublicNotices, type PublicNoticeSummary } from '../api/notice';
import { useDarkMode } from '../contexts/DarkModeContext';
import './NoticeTable.css';

const PAGE_SIZE = 10;
const NEW_TOP_COUNT = 3;

type CategoryFilter = '전체' | '버그수정' | '점검안내' | '업데이트';

function formatDate(value: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value.slice(0, 10).replace(/-/g, '.');
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}.${m}.${day}`;
}

function noticeCategory(item: PublicNoticeSummary): Exclude<CategoryFilter, '전체'> {
  const cat = item.category;
  if (cat === '버그수정' || cat === '점검안내' || cat === '업데이트') return cat;
  return '업데이트';
}

function getBadgeClass(category: string): string {
  if (category === '버그수정') return 'bug';
  if (category === '점검안내') return 'notice';
  return 'update';
}

const NoticeListPage: React.FC = () => {
  const { isDarkMode } = useDarkMode();
  const [items, setItems] = useState<PublicNoticeSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState<CategoryFilter>('전체');
  const [searchText, setSearchText] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchPublicNotices()
      .then(setItems)
      .catch((err) => setError(err instanceof Error ? err.message : '조회 실패'))
      .finally(() => setLoading(false));
  }, []);

  const filteredNotices = useMemo(() => {
    const q = appliedSearch.trim().toLowerCase();
    return items.filter((notice) => {
      const cat = noticeCategory(notice);
      const matchCategory = category === '전체' || cat === category;
      const matchSearch = !q || notice.title.toLowerCase().includes(q);
      return matchCategory && matchSearch;
    });
  }, [items, category, appliedSearch]);

  /** API는 최신순 — 전체 목록 기준 상위 3건에 NEW */
  const newIds = useMemo(
    () => new Set(items.slice(0, NEW_TOP_COUNT).map((n) => n.id)),
    [items],
  );

  const totalPages = Math.max(1, Math.ceil(filteredNotices.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pageItems = filteredNotices.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  useEffect(() => {
    setCurrentPage(1);
  }, [category, appliedSearch]);

  const runSearch = (e?: React.FormEvent) => {
    e?.preventDefault();
    setAppliedSearch(searchText);
  };

  const pageNumbers = useMemo(() => {
    const maxButtons = 10;
    let start = Math.max(1, safePage - Math.floor(maxButtons / 2));
    let end = Math.min(totalPages, start + maxButtons - 1);
    start = Math.max(1, end - maxButtons + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [safePage, totalPages]);

  const totalLabel = filteredNotices.length.toLocaleString('ko-KR');
  const pageLabel = `${safePage.toLocaleString('ko-KR')} / ${totalPages.toLocaleString('ko-KR')}`;

  return (
    <div className={`notice-container${isDarkMode ? ' dark' : ''}`}>
      <div className="notice-header">
        <div className="notice-header-left">
          <h1>
            <span className="notice-header-icon" aria-hidden>
              <CampaignIcon fontSize="inherit" />
            </span>
            공지사항
          </h1>
        </div>
        <Link to="/" className="notice-back">
          Home
        </Link>
      </div>

      <form className="search-bar" onSubmit={runSearch}>
        <select
          className="category-select"
          value={category}
          onChange={(e) => setCategory(e.target.value as CategoryFilter)}
          aria-label="카테고리 필터"
        >
          <option>전체</option>
          <option>버그수정</option>
          <option>점검안내</option>
          <option>업데이트</option>
        </select>
        <input
          type="text"
          placeholder="검색어를 넣어주세요"
          className="search-input"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          aria-label="검색어를 넣어주세요"
        />
        <button type="submit" className="search-btn">
          검색
        </button>
      </form>

      {loading && <p className="notice-muted">불러오는 중…</p>}
      {error && <p className="notice-error">{error}</p>}
      {!loading && !error && (
        <div className="notice-count-row">
          <span className="notice-count">
            총 게시물 {totalLabel} , 페이지 {pageLabel}
          </span>
        </div>
      )}
      {!loading && !error && filteredNotices.length === 0 && (
        <p className="notice-muted">등록된 공지가 없습니다.</p>
      )}

      {pageItems.length > 0 && (
        <div className="table-wrapper">
          <table className="notice-table">
            <tbody>
              {pageItems.map((notice, index) => {
                const cat = noticeCategory(notice);
                const isNew = newIds.has(notice.id);
                const no = filteredNotices.length - ((safePage - 1) * PAGE_SIZE + index);
                return (
                  <tr key={notice.id} className={isNew ? 'table-row new' : 'table-row'}>
                    <td className="num">{no}</td>
                    <td className="title">
                      <Link to={`/notices/${notice.id}`} className="title-link">
                        <span className={`badge ${getBadgeClass(cat)}`}>{cat}</span>
                        {notice.title}
                        {isNew && <span className="badge-new">NEW</span>}
                      </Link>
                    </td>
                    <td className="date">{formatDate(notice.created_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && !error && filteredNotices.length > 0 && (
        <nav className="pagination" aria-label="페이지 탐색">
          <button
            type="button"
            className="pagination-btn prev-btn"
            disabled={safePage <= 1}
            onClick={() => setCurrentPage(1)}
            aria-label="이전 페이지"
          >
            <span>&lt;&lt;</span>
          </button>
          <button
            type="button"
            className="pagination-btn prev-btn"
            disabled={safePage <= 1}
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            aria-label="이전"
          >
            <span>&lt;</span>
          </button>
          {pageNumbers.map((n) => (
            <button
              key={n}
              type="button"
              className={`pagination-btn${n === safePage ? ' active' : ''}`}
              onClick={() => setCurrentPage(n)}
              aria-current={n === safePage ? 'page' : undefined}
            >
              {n}
            </button>
          ))}
          <button
            type="button"
            className="pagination-btn next-btn"
            disabled={safePage >= totalPages}
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            aria-label="다음"
          >
            <span>&gt;</span>
          </button>
          <button
            type="button"
            className="pagination-btn next-btn"
            disabled={safePage >= totalPages}
            onClick={() => setCurrentPage(totalPages)}
            aria-label="다음 페이지"
          >
            <span>&gt;&gt;</span>
          </button>
        </nav>
      )}

    </div>
  );
};

export default NoticeListPage;
